import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import SuggestingInput from 'react/elements/SuggestingInput';
import ingredientSuggestions from './ingredientSuggestions';
import MaterialIcon from 'react/elements/MaterialIcon';
import DragHandle from 'react/elements/DragHandle';
import { setMealIdeaIngredients, MEAL_IDEAS_LOADED } from 'redux/actions/mealIdeas';
import { selectMealIdeasIndex } from '../../../redux/food/selectors';
import useOffline, { NEEDS_CONNECTION_TITLE, onlineOnly, onlineOnlyField, useSaveWhenOnline } from '../../hooks/useOffline';

/**
 * An idea's ingredients. Every change posts the whole ordered list: a row keeps its id, a new row
 * has none, and a row left out is archived on the server. Reordering is a drag, handled in
 * `DashboardPage.onDragEnd`.
 *
 * An ingredient's name opens it, through `onOpen(ingredient, element)`: renaming it, and setting how
 * much of it this idea takes, happen there (GroceryItemEditor). The amount and notes it has show
 * under the name.
 *
 * The new-ingredient field asks for an Enter key. Without `enterkeyhint`, Android offers "Next"
 * because the notes field sits below, and that moves focus there without sending Enter, so the
 * field cannot be used to type one ingredient after another. Lowercase passes the attribute through
 * a React that does not know it. As it is typed it suggests names the budget already uses
 * (ingredientSuggestions), so an ingredient joins the item it means rather than starting a near-twin.
 *
 * The box beside each handle picks it for "Add to grocery list"; the panel owns the picking, as
 * `selected` and `onSelect`. An ingredient already on the active list from this idea shows a cart in
 * place of the box, since adding it again would add nothing. One already on the list for something
 * else keeps its box, since adding links this idea to that item, and gets a faint cart saying where
 * the item came from. `onList` is `ingredientsOnList`'s answer.
 *
 * The cart is a button: `onUnlist(ingredient)` takes the ingredient off the list, keeping it here and
 * not checking it off. `unlisting` holds the ids on their way off, whose carts are disabled meanwhile.
 *
 * Under a name it has been bought by, the day it was last checked off the grocery list, from
 * `lastBought` (`ingredientsLastBought`'s answer).
 *
 * An ingredient whose item is a generic with kinds, or one of them, can be narrowed to one kind or
 * widened back to any (its `generic` and `kinds`, from the server). The choice renames the
 * ingredient to that item, as renaming it only in this idea does, so the grocery list follows.
 *
 * Every change to the list is online-only, so offline the remove and kind controls, the carts and the
 * drag are disabled, titled "Needs a connection", and the new-ingredient field is read-only; a name
 * left in it offline is added once back online. Picking still works.
 */
export default function MealIdeaIngredientList({ idea, selected = new Set(), onSelect = () => {}, onList = new Map(), lastBought = new Map(), onOpen = () => {}, onUnlist = () => {}, unlisting = new Set() }) {
	const dispatch = useDispatch();
	const [newName, setNewName] = useState('');
	const ideasById = useSelector((state) => selectMealIdeasIndex(state).ideasById);
	const groceryRows = useSelector((state) => state.food.entities.groceryRows);
	const groceryItems = useSelector((state) => state.food.entities.groceryItems);
	const lastBoughtByName = useSelector((state) => state.food.entities.lastBoughtByName);
	const lastBoughtByUnlinkedName = useSelector((state) => state.food.entities.lastBoughtByUnlinkedName);
	const adding = useRef(false);
	const offline = useOffline();
	const whenOnline = useSaveWhenOnline(offline);
	const ingredients = idea.ingredients || [];

	const save = (list) => dispatch(setMealIdeaIngredients(idea.id, list));

	// Made to the list as it stands when the save goes, which may be after another save answers.
	const remove = (ingredient) => {
		save((list) => list.filter((row) => row.id !== ingredient.id));
	};

	const choose = (ingredient, name) => {
		save((list) => list.map((row) => (row.id === ingredient.id ? { ...row, name } : row)));
	};

	/** Enter and then leaving the field would otherwise add the same ingredient twice. */
	const add = (name = newName) => {
		const trimmed = name.trim();
		if(!trimmed || adding.current) {
			return;
		}
		adding.current = true;
		save((list) => [...list, { name: trimmed }]).then((action) => {
			adding.current = false;
			if(action && action.type === MEAL_IDEAS_LOADED) {
				setNewName('');
			}
		});
	};

	const pickable = ingredients.filter((ingredient) => ingredient.id && !onList.get(ingredient.id)?.linked);
	const pickedCount = pickable.filter((ingredient) => selected.has(ingredient.id)).length;
	const allPicked = pickable.length > 0 && pickedCount === pickable.length;
	const selectAll = useRef(null);
	useEffect(() => {
		if(selectAll.current) {
			selectAll.current.indeterminate = pickedCount > 0 && !allPicked;
		}
	});

	const toggle = (ingredient) => {
		const next = new Set(selected);
		if(next.has(ingredient.id)) {
			next.delete(ingredient.id);
		} else {
			next.add(ingredient.id);
		}
		onSelect(next);
	};

	const toggleAll = () => onSelect(allPicked ? new Set() : new Set(pickable.map((ingredient) => ingredient.id)));

	return (
		<div className='meal_idea_ingredients'>
			{pickable.length > 1 &&
			<label className='meal_idea_ingredients__select-all'>
				<input type='checkbox' className='filled-in' ref={selectAll} checked={allPicked} onChange={toggleAll} />
				<span>Select all</span>
			</label>
			}
			<Droppable droppableId={`mealIdeaIngredients-${idea.id}`} type='MEAL_IDEA_INGREDIENTS'>
				{(provided) => (
					<div ref={provided.innerRef} {...provided.droppableProps}>
						{ingredients.map((ingredient, index) => (
							<Draggable
								key={ingredient.id}
								draggableId={`mealIdeaIngredient-${ingredient.id}`}
								index={index}
								isDragDisabled={offline}
							>
								{(draggable) => (
									<div
										className='meal_idea_ingredient'
										ref={draggable.innerRef}
										{...draggable.draggableProps}
									>
										<DragHandle className='' {...draggable.dragHandleProps} title={offline ? NEEDS_CONNECTION_TITLE : undefined} />
										{onList.get(ingredient.id)?.linked ?
											<button
												type='button'
												className='btn-flat meal_idea_ingredient__on-list'
												title={offline ? NEEDS_CONNECTION_TITLE : `On the grocery list. Take ${ingredient.name} off it`}
												aria-label={`Take ${ingredient.name} off the grocery list`}
												disabled={offline || unlisting.has(ingredient.id)}
												onClick={() => onUnlist(ingredient)}
											>
												<MaterialIcon className='meal_idea_ingredient__on-list-cart'>shopping_cart</MaterialIcon>
												<MaterialIcon className='meal_idea_ingredient__on-list-remove'>remove_shopping_cart</MaterialIcon>
											</button> :
											<label className='meal_idea_ingredient__pick'>
												<input
													type='checkbox'
													className='filled-in'
													aria-label={`Pick ${ingredient.name}`}
													checked={selected.has(ingredient.id)}
													onChange={() => toggle(ingredient)}
												/>
												<span></span>
											</label>
										}
										<div className='meal_idea_ingredient__text'>
											<button
												type='button'
												className='btn-flat meal_idea_ingredient__open'
												title={`About ${ingredient.name}`}
												onClick={(event) => onOpen(ingredient, event.currentTarget)}
											>
												{ingredient.name}
											</button>
											{!!(ingredient.quantity || ingredient.notes) &&
											<span className='meal_idea_ingredient__amount'>
												{[ingredient.quantity, ingredient.notes].filter(Boolean).join(' \u00B7 ')}
											</span>
											}
											{!!ingredient.generic &&
											<KindChoice ingredient={ingredient} offline={offline} onChoose={(name) => choose(ingredient, name)} />
											}
											{lastBought.has(ingredient.id) &&
											<span className='meal_idea_ingredient__last-bought'>Last bought {lastBought.get(ingredient.id).format('LL')}</span>
											}
										</div>
										{!!onList.get(ingredient.id)?.elsewhere &&
										<MaterialIcon
											className='meal_idea_ingredient__also-on-list'
											title={`${ingredient.name} is already on the grocery list, ${onList.get(ingredient.id).elsewhere}. Adding this idea adds it to that item.`}
										>
											shopping_cart
										</MaterialIcon>
										}
										{offline ?
										<MaterialIcon className='is-disabled' title={NEEDS_CONNECTION_TITLE} aria-disabled='true'>close</MaterialIcon> :
										<MaterialIcon
											className='clickable'
											title={`Remove ${ingredient.name}`}
											onClick={() => remove(ingredient)}
										>
											close
										</MaterialIcon>
										}
									</div>
								)}
							</Draggable>
						))}
						{provided.placeholder}
					</div>
				)}
			</Droppable>
			<div className='input-field meal_idea_ingredients__new'>
				<MaterialIcon className='meal_idea_ingredients__new-icon'>add</MaterialIcon>
				<SuggestingInput
					aria-label='New ingredient'
					placeholder={offline ? 'Adding ingredients needs a connection' : 'Add an ingredient'}
					{...onlineOnlyField(offline)}
					enterkeyhint='enter'
					value={newName}
					onChange={setNewName}
					suggestions={ingredientSuggestions(ideasById, Object.values(groceryRows), ingredients, newName, [
						...Object.values(groceryItems || {}).map((item) => item.name),
						...Object.keys(lastBoughtByUnlinkedName || {}),
						...Object.keys(lastBoughtByName || {}),
					])}
					onSubmit={add}
					menuId={`meal-idea-ingredient-names-${idea.id}`}
					menuClassName='meal_idea_ingredients__suggestions'
					onBlur={() => whenOnline('add', () => add())}
				/>
			</div>
		</div>
	);
}

/** Which of a generic's kinds the idea wants, or any. */
function KindChoice({ ingredient, offline, onChoose }) {
	const { generic, kinds } = ingredient;
	const options = [{ id: generic.id, name: generic.name, label: `Any ${generic.name.toLowerCase()}` }, ...kinds.map((kind) => ({ ...kind, label: kind.name }))];
	return (
		<select
			className='browser-default meal_idea_ingredient__kind'
			aria-label={`Which ${generic.name.toLowerCase()}`}
			{...onlineOnly(offline)}
			value={ingredient.grocery_item_id}
			onChange={(event) => {
				const option = options.find((each) => String(each.id) === event.target.value);
				if(option && option.id !== ingredient.grocery_item_id) {
					onChoose(option.name);
				}
			}}
		>
			{options.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
		</select>
	);
}
